import classNames from 'classnames';
import React, { Children, useEffect, useState } from 'react';
import { useMatchMedia } from '@udemy/hooks';
import { AutoPlayCarousel, Carousel, CarouselNextButton, CarouselPreviousButton, dispatchCarouselNextPageEvent, dispatchCarouselPreviousPageEvent } from '@udemy/react-structure-components';
import { SlideIndicatorDashes, SlideIndicatorDots } from '../slide-indicator/slide-indicator.react-component';
import styles from "./hero-banner.module.css";
export const HeroBannerSlideLayoutType = {
  SPLIT: 'split',
  WIDE: 'wide',
  WIDE_DARK: 'wide-dark'
};

/** React prop interface for `HeroBanner` component */

const SplitHeroBanner = ({
  children,
  onPreviousClick,
  onNextClick,
  showSlideIndicator = false
}) => {
  const isLgMin = useMatchMedia('lg-min');
  const hasCoarsePointer = useMatchMedia('(any-pointer: coarse)');
  const [pagerEventTarget, setPagerEventTarget] = useState();
  const [visiblePageIndex, setVisiblePageIndex] = useState(0);
  const [paused, setPaused] = useState(false);
  const [disableNextButton, setDisableNextButton] = useState(false);
  const [disablePrevButton, setDisablePrevButton] = useState(false);
  const slides = Children.toArray(children).length;
  const disableAutoplay = showSlideIndicator && isLgMin;
  const CarouselComponent = disableAutoplay ? Carousel : AutoPlayCarousel;
  const handleMutation = event => {
    setVisiblePageIndex(event.visiblePageIndex);
    if (disableAutoplay) {
      if (event.isLastPage && event.visiblePageIndex !== null) {
        setDisableNextButton(true);
      } else if (disableNextButton) {
        setDisableNextButton(false);
      }
      if (event.visiblePageIndex === 0) {
        setDisablePrevButton(true);
      } else if (disablePrevButton) {
        setDisablePrevButton(false);
      }
    }
  };
  const handleIntervalUpdate = event => {
    setPaused(event.isPaused);
  };
  const customNextClick = () => {
    dispatchCarouselNextPageEvent(pagerEventTarget);
  };
  const customPrevClick = () => {
    dispatchCarouselPreviousPageEvent(pagerEventTarget);
  };
  useEffect(() => {
    // `EventTarget` is not supported nodeJS 14.*, so instantiate it client-side in browser.
    setPagerEventTarget(new EventTarget());
  }, []);
  return /*#__PURE__*/React.createElement("div", {
    className: styles.wrapper
  }, /*#__PURE__*/React.createElement(CarouselComponent, Object.assign({
    gridFullWidthItems: true,
    onMutation: handleMutation,
    onNextClick: onNextClick,
    onPreviousClick: onPreviousClick,
    pageByFullWidth: true,
    pagerEventTarget: pagerEventTarget,
    pagerButtonClassName: styles['pager-button'],
    showPager: !showSlideIndicator
  }, !disableAutoplay ? {
    onIntervalUpdate: handleIntervalUpdate,
    prioritizeTouch: showSlideIndicator && !!hasCoarsePointer
  } : {}), children), /*#__PURE__*/React.createElement("div", {
    className: classNames(styles['pager-buttons-wrapper'], {
      [styles['slide-dots-indicator-wrapper']]: showSlideIndicator
    })
  }, /*#__PURE__*/React.createElement(CarouselPreviousButton, {
    className: classNames(styles['slide-indicator-pager'], {
      [styles.disabled]: disablePrevButton
    }),
    disabled: disablePrevButton,
    onClick: customPrevClick,
    udStyle: 'white-solid'
  }), showSlideIndicator && /*#__PURE__*/React.createElement(SlideIndicatorDots, {
    pageCount: slides,
    visiblePageIndex: visiblePageIndex
  }), /*#__PURE__*/React.createElement(CarouselNextButton, {
    className: classNames(styles['slide-indicator-pager'], {
      [styles.disabled]: disableNextButton
    }),
    disabled: disableNextButton,
    onClick: customNextClick,
    udStyle: 'white-solid'
  })), showSlideIndicator && !isLgMin && /*#__PURE__*/React.createElement(SlideIndicatorDashes, {
    pageCount: slides,
    visiblePageIndex: visiblePageIndex,
    isPaused: paused
  }));
};
const WideHeroBanner = ({
  onNextClick,
  onPreviousClick,
  children
}) => {
  const hasFinePointer = useMatchMedia('(any-pointer: fine)');
  const hasCoarsePointer = useMatchMedia('(any-pointer: coarse)');
  const hasPrimaryCoarsePointer = useMatchMedia('(pointer: coarse)');
  return /*#__PURE__*/React.createElement("div", {
    className: styles['wrapper-wide']
  }, /*#__PURE__*/React.createElement(AutoPlayCarousel, {
    onNextClick: onNextClick,
    onPreviousClick: onPreviousClick,
    pagerButtonClassName: styles['pager-button--wide'],
    pageByFullWidth: true,
    gridFullWidthItems: true,
    prioritizeTouch: !!hasPrimaryCoarsePointer,
    showPager: !!hasFinePointer,
    allowScroll: !!hasCoarsePointer,
    pagerButtonSize: "medium"
  }, children));
};

/**
 * Hero Banner
 *
 * @param children HeroBannerSlide or WideHeroBannerSlide if you're using the wide layout
 * @param slideLayout Layout type of the hero banner. It can be either 'wide' or 'split'
 * @param props
 * @constructor
 */
export const HeroBanner = ({
  children,
  slideLayout,
  ...props
}) => {
  const isWideLayout = slideLayout === HeroBannerSlideLayoutType.WIDE || slideLayout === HeroBannerSlideLayoutType.WIDE_DARK;
  const BannerComponent = isWideLayout ? WideHeroBanner : SplitHeroBanner;
  return /*#__PURE__*/React.createElement(BannerComponent, props, children);
};
//# sourceMappingURL=hero-banner.react-component.js.map